/**
 * Every club's API tokens, for Priori.
 *
 * WHY THIS EXISTS SEPARATELY. Helping a club with a credential problem should
 * not require impersonating one of their people: an impersonated session is
 * read-only by design, so it could not revoke a leaked token anyway, and it
 * would put a club member's name on an action Priori took.
 *
 * BOTH HALVES ARE platform.admin. Reading every club's credentials — names,
 * scopes, owners, expiry — is administration rather than visibility, and
 * admin_api.py's own guard test refuses a route here on a weaker rule.
 *
 * MINTING IS ABSENT ON PURPOSE. A club's credential is created by that club,
 * so the audit row names a person in a club rather than "the platform". Staff
 * who genuinely need to create one switch into the club and use the club page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../lib/api'
import './admin.css'
import './api-tokens.css'
import type { Session } from '../lib/api'

type AdminToken = {
  id: number
  name: string
  kind: 'personal' | 'service'
  prefix: string
  scopes: string[]
  status: 'active' | 'expired' | 'revoked'
  organization: string | null
  organization_name: string | null
  owner: string | null
  created_by: string | null
  created_at: string | null
  expires_at: string | null
  last_used_at: string | null
}

function when(iso: string | null): string {
  if (!iso) return '—'
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

/** Days until expiry, negative once past. Used to flag the ones about to
 *  break an integration nobody is watching. */
function daysLeft(iso: string | null): number | null {
  if (!iso) return null
  const at = new Date(iso).getTime()
  if (Number.isNaN(at)) return null
  return Math.round((at - Date.now()) / 86_400_000)
}

function ManageApiTokens({ session }: { session: Session }) {
  const [tokens, setTokens] = useState<AdminToken[] | null>(null)
  const [mayRevoke, setMayRevoke] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [showRevoked, setShowRevoked] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await api<{ tokens: AdminToken[]; may_revoke: boolean }>(
        '/api/v1/admin/api-tokens')
      setTokens(res.tokens)
      setMayRevoke(res.may_revoke)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The tokens could not be read.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (tokens ?? []).filter((t) => {
      if (!showRevoked && t.status !== 'active') return false
      if (!q) return true
      return [t.name, t.organization, t.organization_name, t.owner, t.created_by, t.prefix]
        .some((v) => (v ?? '').toLowerCase().includes(q))
    })
  }, [tokens, query, showRevoked])

  const revoke = async (token: AdminToken) => {
    const why = window.prompt(
      `Revoke “${token.name}” for ${token.organization ?? 'this club'}?\n\n`
      + 'Anything the club is running with it stops working immediately.\n'
      + 'Reason (recorded in the audit trail, and the club may ask):')
    if (!why) return
    try {
      await api(`/api/v1/admin/api-tokens/${token.id}/revoke`,
                { method: 'POST', body: JSON.stringify({ reason: why }) })
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The token could not be revoked.')
    }
  }

  return (
    <>
      <h2 className="st-h2">Tokens across every club</h2>
      <p className="page-sub">
        Every club&rsquo;s tokens, so a credential problem can be answered
        without impersonating anyone. Creating one is done from inside the
        club, so the audit trail names a person there rather than the platform.
      </p>

      {error && <p className="error">{error}</p>}

      <div className="adm-bar">
        <label className="adm-field" style={{ marginBottom: 0 }}>
          <span className="t-label">Find</span>
          <input className="adm-input" style={{ minWidth: 320 }}
                 value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="club, name, owner or prefix" />
        </label>
        <label className="tok-toggle">
          <input type="checkbox" checked={showRevoked}
                 onChange={(e) => setShowRevoked(e.target.checked)} />
          Show expired and revoked
        </label>
        <span className="adm-bar__spacer" />
        <span className="t-data" style={{ color: 'var(--color-text-faint)' }}>
          {tokens ? `${shown.length} of ${tokens.length}` : ''}
        </span>
      </div>

      <table className="adm-table tok-table">
        <thead>
          <tr>
            <th>Club</th><th>Name</th><th>Kind</th><th>Prefix</th>
            <th>Owner</th><th>Created by</th><th>Expires</th>
            <th>Last used</th><th>Status</th><th />
          </tr>
        </thead>
        <tbody>
          {tokens === null && (
            <tr><td colSpan={10} className="adm-empty">Loading…</td></tr>
          )}
          {tokens !== null && shown.length === 0 && (
            <tr><td colSpan={10} className="adm-empty">Nothing matches.</td></tr>
          )}
          {shown.map((t) => {
            const left = daysLeft(t.expires_at)
            return (
              <tr key={t.id}>
                <td className="adm-primary">{t.organization ?? '—'}</td>
                <td>{t.name}</td>
                <td><span className={`adm-chip adm-chip--${t.kind}`}>{t.kind}</span></td>
                <td><code className="tok-prefix">{t.prefix}…</code></td>
                <td>{t.owner ?? <span className="adm-chip__role">the club</span>}</td>
                <td className="adm-when">{t.created_by ?? '—'}</td>
                <td className="adm-when">
                  {when(t.expires_at)}
                  {/* An integration that dies quietly next week is worth
                      saying out loud while there is still time to rotate. */}
                  {t.status === 'active' && left !== null && left <= 14 && (
                    <span className="tok-soon">{left}d left</span>
                  )}
                </td>
                <td className="adm-when">
                  {t.last_used_at ? when(t.last_used_at)
                    : <span className="adm-chip__role">never</span>}
                </td>
                <td>
                  <span className={`adm-chip tok-status tok-status--${t.status}`}>
                    {t.status}
                  </span>
                </td>
                <td>
                  {mayRevoke && t.status === 'active' && (
                    <button className="adm-btn adm-btn--danger"
                            onClick={() => { void revoke(t) }}>Revoke</button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {!mayRevoke && (
        <p className="page-sub">
          You can read these but not revoke them — revoking is a platform-admin
          action. Signed in as {session.user.email}.
        </p>
      )}
    </>
  )
}

export default ManageApiTokens
